import React, { Fragment, type ReactNode, type RefObject, useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex } from '@atlaskit/primitives';

import { useExtensionsState } from '../../../controllers/use-extensions-state';
import { useProductDependencies } from '../../../controllers/use-product-dependencies';
import { type Item as ItemType } from '../../../types';
import { checkItems } from '../../config-panel/required-parameters-validators';
import { DraggableItemsZone } from '../draggable-items/draggable-items-zone';

import { Item, type ItemProps } from './item';

const upAccelerator = 'mod+option+shift+[';
const downAccelerator = 'mod+option+shift+]';

export type ItemsListBuilderProps = {
	canDeleteItem?: boolean;
	items: ItemType[];
	itemsListRef?: RefObject<HTMLDivElement>;
	renderItemContent(itemParameters: ItemType, isItemDisabled: boolean): ReactNode;
	shouldUseCardV2UI?: boolean;
	updateItems(items: ItemType[]): void;
} & Pick<ItemProps, 'actionSubjectIdPostFix' | 'analyticsSource' | 'removeItem' | 'itemLabel'>;

export const ItemsListBuilder = ({
	items,
	removeItem,
	analyticsSource,
	actionSubjectIdPostFix,
	itemsListRef,
	shouldUseCardV2UI,
	updateItems,
	itemLabel,
	canDeleteItem = true,
	renderItemContent,
}: ItemsListBuilderProps): React.JSX.Element => {
	const { shortcutListener } = useProductDependencies();
	const isDeleteDisabled = items?.length === 1;

	const [
		{ selectedToEditCardId },
		{ setIsInViewMode, setSelectedToEditCardId, setIsInAutoFocusMode },
	] = useExtensionsState();

	const firstItemId = items?.[0]?.cardId || null;
	useEffect(() => {
		// the order is important here
		// view mode should goes first to allow the active item to be set
		setIsInViewMode(false);
		if (selectedToEditCardId !== 'dragged-item') {
			setSelectedToEditCardId(firstItemId);
		}

		return () => {
			// the order is important here
			// view mode should goes second to allow the active item to reset
			setSelectedToEditCardId(null);
			setIsInViewMode(true);
			setIsInAutoFocusMode(true);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [firstItemId, setIsInViewMode, setSelectedToEditCardId, setIsInAutoFocusMode]);

	const setDraggedItem = () => setSelectedToEditCardId('dragged-item');

	const swapItems = (source: number, target: number) => {
		const newItems = [...items];

		const temp = newItems[target];
		newItems[target] = newItems[source];
		newItems[source] = temp;

		updateItems(newItems);
	};

	const [focusedItem, setFocusedItem] = useState<number | undefined>(undefined);

	const onMoveUp = () => {
		if (focusedItem !== undefined) {
			setDraggedItem();
			swapItems(focusedItem, focusedItem - 1);
			setFocusedItem(focusedItem - 1);
		}
	};
	const onMoveDown = () => {
		if (focusedItem !== undefined) {
			setDraggedItem();
			swapItems(focusedItem, focusedItem + 1);
			setFocusedItem(focusedItem + 1);
		}
	};

	const isFirstItemFocused = focusedItem === 0;
	const isLastItemFocused = focusedItem === items?.length - 1;

	const ShortcutListener = shortcutListener || Fragment;

	return (
		<Flex
			direction="column"
			testId="items-list-builder"
			gap={shouldUseCardV2UI ? undefined : 'space.200'}
			ref={itemsListRef}
		>
			{checkItems(items) && (
				<DraggableItemsZone
					analyticsSource={analyticsSource}
					defaultItems={items}
					setDraggedItem={setDraggedItem}
					updateItems={updateItems}
					shouldUseCardV2UI={shouldUseCardV2UI}
				>
					{!isFirstItemFocused && shortcutListener && (
						<ShortcutListener accelerator={upAccelerator} listener={onMoveUp} />
					)}
					{!isLastItemFocused && shortcutListener && (
						<ShortcutListener accelerator={downAccelerator} listener={onMoveDown} />
					)}
					{items.map((itemParameters: ItemType, order) => {
						return (
							<Item
								actionSubjectIdPostFix={actionSubjectIdPostFix}
								analyticsSource={analyticsSource}
								itemParameters={itemParameters}
								isDeleteDisabled={isDeleteDisabled}
								isExpanded={selectedToEditCardId === itemParameters.cardId}
								isFirstItem={order === 0}
								isLastItem={order === items.length - 1}
								key={itemParameters.cardId}
								onFocus={() => setFocusedItem(order)}
								onBlur={() => setFocusedItem(undefined)}
								order={order + 1}
								removeItem={removeItem}
								renderItemContent={renderItemContent}
								setDraggedItem={setDraggedItem}
								setExpandedItem={setSelectedToEditCardId}
								swapItems={swapItems}
								shouldUseCardV2UI={shouldUseCardV2UI}
								itemLabel={itemLabel}
								canDeleteItem={canDeleteItem}
							/>
						);
					})}
				</DraggableItemsZone>
			)}
		</Flex>
	);
};
